import { trigger, transition, style, animate } from '@angular/animations';
import { Component, Input, OnDestroy, OnInit, QueryList, SimpleChanges, ViewChildren, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SharedService } from '@shared/services/shared.service';
import { IEntityOwner } from 'app/models/licenses/entity-owner';
import { IStatus } from 'app/models/licenses/status';
import { EntityOwnerService } from 'app/services/license/in-home-care/entity-owner.service';
import { AddressComponent } from '../address/address.component';
import { DocumentComponent } from '../document/document.component';
import { IAddress } from 'app/models/address/address';
import { ILicenseChangeDialog } from 'app/components/license/in-home-care/in-home-care-change/in-home-care-change-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { IIHCOwnerChangeDialog, InHomeCareOwnerChangeDialogComponent } from 'app/components/license/in-home-care/in-home-care-owner-change/in-home-care-owner-change.component';

@Component({
  selector: 'entity-owner',
  templateUrl: './entity-owner.component.html',
  styleUrls: ['./entity-owner.component.scss'],
  animations: [
    trigger('slideRight', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
      ])
    ])
  ]
})

export class EntityOwnerComponent implements OnDestroy, OnInit{
  @ViewChildren(DocumentComponent) documents!: QueryList<DocumentComponent>;
  @ViewChildren(AddressComponent) addresses!: QueryList<AddressComponent>;
  @Input() entityId: number = 0;
  @Input() entityGuid: string = '';
  @Input() entityType: string = '';
  @Input() parentId: number = 0;
  @Input() isSubunit: boolean = false;
  @Input() canChange: boolean = true;
  @Input() isChange: boolean = false;
  @Input() statuses: IStatus[] = [];
  @Input() isRenewal: boolean = false;

  public managingOwner: boolean = false;
  public isOutOfState: boolean = false;
  public newOwnerCreated: boolean = false;
  public includeArchived: boolean = false;
  public entityOwnerDocTypes: string[] = ['Background Check Request']
  public owner: IEntityOwner = {
    id: 0,
    parentId: 0,
    firstName: '',
    lastName: '',
    phone: '',
    fax: '',
    taxId: '',
    contactWithClient: null,
    archived: false
  }
  public owners: IEntityOwner[] = [];
  public parentOwners: IEntityOwner[] = [];
  public ownerForm = new FormGroup({
    firstName: new FormControl('', Validators.required),
    phone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    fax: new FormControl('', [Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    taxId: new FormControl('', Validators.required),
    contactWithClient: new FormControl<boolean | null>(null)
  });

  public sharedService = inject(SharedService);
  public ownerService = inject(EntityOwnerService);
  public dialog = inject(MatDialog);

  ngOnInit(): void {
    this.getOwners(this.includeArchived);
    // if(this.isRenewal && this.entityType === this.sharedService.hospitalLicense){
    //   this.ownerForm.get('firstName')?.disable();
    //   this.ownerForm.get('taxId')?.disable();
    // }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(this.isChange){
      return;
    }
    const change = changes['parentId'];
    if(change === undefined) return;
    if(change.currentValue !== change.previousValue && this.isSubunit){
      this.getOwners(this.includeArchived);
    }
  }

  ngOnDestroy(): void {
    if(this.managingOwner){
      this.saveOwner();
    }
  }

  getOwners(includeArchived: boolean): void {
    this.includeArchived = includeArchived;
    let lookupId = this.entityId;
    if(this.isSubunit){
      if(this.parentId === 0) {
        this.sharedService.openNotice('Add a parent agency to view parent owners.');
        return;
      }
      lookupId = this.parentId;
    }

    this.ownerService.getEntityOwners(lookupId, this.entityType, this.includeArchived).subscribe({
      next: response => this.owners = response,
      error: e => console.log('get entity owners error', e),
      complete: () => {
        if(this.owners.length === 0 && this.isChange){
          this.createOwner();
        }
      }
    });
  }

  selectOwner(owner: IEntityOwner): void {
    this.owner = owner;
    this.managingOwner = true;
    this.updateOwnerForm(owner);
  }

  toggleOwnerForm(adding: boolean): void{
    this.managingOwner = adding;
    this.updateOwnerForm(this.getEmptyOwner());
    if(!adding){
      if(this.newOwnerCreated){
        this.deleteOwner(this.owner.id);
      }
      else{
        this.owner = this.getEmptyOwner();
      }
    }
  }

  getEmptyOwner(): IEntityOwner {
    return {
      id: 0,
      parentId: 0,
      firstName: '',
      lastName: '',
      phone: '',
      fax: '',
      taxId: '',
      contactWithClient: null,
      archived: false
    }
  }

  updateOwnerForm(owner: IEntityOwner): void {
    this.ownerForm.reset({
      firstName: owner.firstName,
      phone: owner.phone,
      fax: owner.fax,
      taxId: owner.taxId,
      contactWithClient: owner.contactWithClient
    });
    if(this.locked && !this.isChange){
      this.ownerForm.get('firstName')?.disable();
    }
  }

  updateOwner(): void {
    let form = this.ownerForm.getRawValue();
    this.owner.firstName = form.firstName ?? '';
    this.owner.phone = form.phone ?? '';
    this.owner.fax = form.fax ?? '';
    this.owner.taxId = form.taxId ?? '';
    this.owner.contactWithClient = form.contactWithClient ?? null;
  }

  createOwner(): void {
    this.owner.parentId = this.entityId;
    this.ownerService.createEntityOwner(this.owner, this.entityType).subscribe({
      next: response => {
        this.owners.push(response);
        this.owner = response;
      },
      error: e => {
        this.sharedService.toastr.error("Error Creating Entity Owner");
        console.log('error creating entity owner', e);
      },
      complete: () => {
        this.toggleOwnerForm(true);
        this.newOwnerCreated = true;
      }
    });
  }

  saveOwner(): void {
    this.updateOwner();
    this.ownerService.updateEntityOwner(this.owner).subscribe({
      complete: () => {
        this.newOwnerCreated = false;
        this.toggleOwnerForm(false);
      },
      error: e => {
        this.sharedService.toastr.error("Could Not Save Owner");
        console.log('error updating entity owner', e);
      }
    });
  }

  archiveOwner(owner: IEntityOwner, archive: boolean): void {
    let archiving: string = archive? 'Archive ' : 'Un-Archive ';
    this.sharedService.openConfirm(archiving + 'this entity owner?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.ownerService.archiveEntityOwner(owner.id, archive).subscribe({
            complete: () => {
              owner.archived = archive;
              if(archive){
                this.owners = this.owners.filter(o => o.id !== owner.id);
              }
              this.toggleOwnerForm(false);
            },
            error: e => {
              this.sharedService.toastr.error("Could Not Archive Owner");
              console.log('error archiving entity owner', e);
            }
          });
        }
      }
    );
  }

  confirmDelete(ownerId: number): void {
    this.sharedService.openConfirm('Delete entity owner ' + this.owner.firstName + ' ?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.deleteOwner(ownerId);
        }
      }
    );
  }

  deleteOwner(ownerId: number): void {
    this.ownerService.deleteEntityOwner(ownerId).subscribe({
      complete: () => {
        this.newOwnerCreated = false;
        this.owners = this.owners.filter(a => a.id !== ownerId);
        this.owner= this.getEmptyOwner();
      },
      error: e => console.log('error deleting owner', e)
    });
  }

  routeChangeRequest(ownerId: number, type: string): void {
    let ownerToChange: IEntityOwner = this.getEmptyOwner();
    if(type === this.sharedService.delete){
      if(this.owners.length === 1){
        this.sharedService.openNotice(`The agency must have at least one owner.
          If you want to change the owner select the option to change the owner instead.`);
        return;
      }
      let tempOwner = this.owners.find(o => o.id === ownerId);
      ownerToChange = tempOwner ? tempOwner : this.getEmptyOwner();
    }

    if(type === this.sharedService.change){
      this.requestOwnerChange(ownerToChange, type);
    }
    else{
      this.requestChange(ownerToChange, type);
    }
  }

  requestChange(owner: IEntityOwner, type: string){
    let dialogData: ILicenseChangeDialog = {
      licenseType: this.sharedService.inHomeCareLicense,
      licenseId: this.entityId,
      licenseGuid: this.entityGuid,
      entityId: owner.id,
      entityType: this.sharedService.entityOwner,
      originalValue: type === this.sharedService.delete? owner : '',
      newValue: '',
      changeAction: type
    }

    this.sharedService.requestChange(dialogData);
  }

  requestOwnerChange(owner: IEntityOwner, type: string): void {
    const dialogData: IIHCOwnerChangeDialog = {
      licenseType: this.sharedService.inHomeCareLicense,
      licenseId: this.entityId,
      licenseGuid: this.entityGuid,
      isSubunit: this.isSubunit,
      isRenewal: this.renewal,
      entityId: owner != undefined? owner.id : 0,
      entityType: this.sharedService.entityOwner,
      originalValue: type === this.sharedService.delete? owner : '',
      newValue: '',
      changeAction: type
    }

    this.dialog.open(InHomeCareOwnerChangeDialogComponent, {
      data: dialogData,
      role: 'dialog',
      ariaLabel: "Change Request Dialog",
      minWidth: !this.sharedService.mobile? '800px' : '',
      maxWidth: !this.sharedService.mobile? '1000px' : '300px',

    });
  }

  updateOutOfState(isOutOfState: boolean): void {
    this.isOutOfState = isOutOfState;
  }

  downloadReferenceDocument(documentType: string): void {
    this.documents.toArray()[0].getReferenceDocument(documentType);
  }

  validateOwner(): string[] {
    let errors: string[] = [];
    if(this.owners.some(o => !o.firstName || !o.phone || !o.taxId)){
      if(this.ownerForm.invalid){
        this.ownerForm.markAllAsTouched();
        errors.push('Owner information is missing. Please fill out all required fields.');
      }
    }
    errors.push(...this.validateAddresses());
    errors.push(...this.validateDocuments());
    return errors;
  }

  validateAddresses(): string[] {
    let errors: string[] = [];

    if(this.entityType !== this.sharedService.hospitalLicense)
      this.addresses.toArray().forEach(address => {
        let addressSet: IAddress[] = address.addresses;
        if(!address.getPhysicalAddress(addressSet)){
          let owner = this.owners.find(o => o.id === address.entityId);
          errors.push('Owner ' + owner?.firstName + ' must have a physical address');
        }
      });

    if(this.entityType == this.sharedService.hospitalLicense)
      this.addresses.toArray().forEach(address => {
        let addressSet: IAddress[] = address.addresses;
        if(!address.getPhysicalAddress(addressSet) && !address.getMailingAddress(addressSet)){
          let owner = this.owners.find(o => o.id === address.entityId);
          errors.push('Owner ' + owner?.firstName + ' must have either a physical or mailing address');
        }
      });

    return errors;
  }

  validateDocuments(): string[] {
    let errors: string[] = [];
    this.documents.toArray().forEach(document => {
      let documentSet = document.documents;
      let tempOwner = this.owners.find(o => o.id === document.entityId);
      let owner = tempOwner ? tempOwner : this.getEmptyOwner();
      if(owner.contactWithClient && !documentSet.some(d => d.types.some(t => t.type === 'Background Check Request'))){
        errors.push('Owner ' + owner.firstName + ' must have a background check request');
      }
    });
    return errors;
  }

  public get locked(): boolean {
    return !this.statuses.some(s => s.label === this.sharedService.started);
  }

  public get approved(): boolean {
    return this.statuses.some(s => s.label === this.sharedService.approved);
  }

  public get renewal(): boolean {
    return this.statuses.some(s => s.label === this.sharedService.renewal);
  }
}
